import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaCheck,
  FaCopy,
  FaDownload,
  FaLightbulb,
  FaMagic,
  FaRedoAlt,
  FaRegClock,
  FaRobot,
} from "react-icons/fa";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "https://tools.microters.com"
).replace(/\/$/, "");

const TONES = [
  "Professional",
  "Engaging",
  "Persuasive",
  "Curious",
  "Urgent",
  "Playful",
  "Authoritative",
  "Emotional",
  "Informative",
  "Witty",
];

const LANGUAGES = [
  "English",
  "Bengali",
  "Hindi",
  "Spanish",
  "French",
  "German",
  "Arabic",
  "Urdu",
  "Portuguese",
  "Indonesian",
];

const HEADLINE_COUNTS = [5, 10, 15, 20];

const EMPTY_USAGE = {
  promptTokens: 0,
  completionTokens: 0,
  totalTokens: 0,
};

const toSafeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeResult = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : {};
  const rawHeadlines = Array.isArray(data.headlines) ? data.headlines : [];
  const headlines = rawHeadlines
    .map((item, index) => {
      const source = typeof item === "string" ? { headline: item } : item || {};
      const headline = String(
        source.headline || source.title || source.text || ""
      ).trim();

      if (!headline) return null;

      return {
        id: `${index}-${headline}`,
        headline,
        type: String(source.type || "Generated").trim(),
        reason: String(source.reason || "").trim(),
      };
    })
    .filter(Boolean);

  return {
    topic: String(data.topic || "").trim(),
    tone: String(data.tone || "").trim(),
    language: String(data.language || "").trim(),
    model: String(data.model || "Oxyy AI").trim(),
    generatedAt: String(data.generatedAt || "").trim(),
    total: headlines.length,
    headlines,
    usage: {
      promptTokens: toSafeNumber(data.usage?.promptTokens),
      completionTokens: toSafeNumber(data.usage?.completionTokens),
      totalTokens: toSafeNumber(data.usage?.totalTokens),
    },
  };
};

const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const safeFileName = (topic) => {
  const cleaned = String(topic || "headlines")
    .toLowerCase()
    .replace(/[^a-z0-9\u0980-\u09ff]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);

  return `${cleaned || "headlines"}-generated-headlines.txt`;
};

const HeadlineGenerator = () => {
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("Professional");
  const [language, setLanguage] = useState("English");
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copiedId, setCopiedId] = useState("");

  const headlines = useMemo(
    () => (Array.isArray(result?.headlines) ? result.headlines : []),
    [result]
  );

  const usage = result?.usage || EMPTY_USAGE;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const cleanTopic = topic.trim();
    if (!cleanTopic) {
      setError("Please enter a topic.");
      toast.error("Please enter a topic.");
      return;
    }

    if (cleanTopic.length < 3) {
      setError("Topic must contain at least 3 characters.");
      toast.error("Topic must contain at least 3 characters.");
      return;
    }

    setLoading(true);
    setError("");
    setCopiedId("");

    console.log("[HEADLINE GENERATOR UI] Sending request.", {
      topicCharacters: cleanTopic.length,
      keywordCharacters: keywords.trim().length,
      tone,
      language,
      count,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/headline-generator`,
        {
          topic: cleanTopic,
          keywords: keywords.trim(),
          tone,
          language,
          count,
        },
        { timeout: 70000 }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Headline generation failed.");
      }

      const normalized = normalizeResult(response.data);
      if (!normalized.headlines.length) {
        throw new Error("The API returned no usable headlines. Please try again.");
      }

      setResult(normalized);
      toast.success(`${normalized.total} headline(s) generated!`);

      console.log("[HEADLINE GENERATOR UI] Request completed.", {
        total: normalized.total,
        model: normalized.model,
        totalTokens: normalized.usage.totalTokens,
      });

      window.setTimeout(() => {
        document
          .getElementById("headline-results")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (requestError) {
      const message =
        requestError.response?.data?.message ||
        (requestError.code === "ECONNABORTED"
          ? "The request timed out. Please try again."
          : requestError.message) ||
        "Could not generate headlines. Please try again.";

      setError(message);
      toast.error(message);

      console.error("[HEADLINE GENERATOR UI] Request failed.", {
        status: requestError.response?.status,
        code: requestError.response?.data?.code || requestError.code,
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyOne = async (item) => {
    try {
      await copyToClipboard(item.headline);
      setCopiedId(item.id);
      toast.success("Headline copied.");
      window.setTimeout(() => setCopiedId(""), 1500);
    } catch (copyError) {
      console.error("[HEADLINE GENERATOR UI] Copy failed.", copyError);
      toast.error("Could not copy the headline.");
    }
  };

  const handleCopyAll = async () => {
    if (!headlines.length) return;

    try {
      await copyToClipboard(
        headlines.map((item, index) => `${index + 1}. ${item.headline}`).join("\n")
      );
      toast.success("All headlines copied.");
    } catch (copyError) {
      console.error("[HEADLINE GENERATOR UI] Copy all failed.", copyError);
      toast.error("Could not copy the headlines.");
    }
  };

  const handleDownload = () => {
    if (!headlines.length) return;

    const lines = [
      "FREE HEADLINE GENERATOR",
      "=======================",
      `Topic: ${result?.topic || topic.trim()}`,
      `Tone: ${result?.tone || tone}`,
      `Language: ${result?.language || language}`,
      `Model: ${result?.model || "Oxyy AI"}`,
      "",
      ...headlines.flatMap((item, index) => [
        `${index + 1}. ${item.headline}`,
        item.type ? `   Type: ${item.type}` : "",
        item.reason ? `   Why it works: ${item.reason}` : "",
        "",
      ]),
    ].filter((line) => line !== "");

    const blob = new Blob([lines.join("\n")], {
      type: "text/plain;charset=utf-8",
    });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = safeFileName(result?.topic || topic);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    toast.success("TXT file downloaded.");
  };

  const handleReset = () => {
    setTopic("");
    setKeywords("");
    setTone("Professional");
    setLanguage("English");
    setCount(10);
    setResult(null);
    setError("");
    setCopiedId("");
  };

  return (
    <main className="hg-page">
      <style>{styles}</style>

  

      <section className="hg-shell">
        <form className="hg-form" onSubmit={handleSubmit} noValidate>
          <div className="hg-field">
            <label htmlFor="hg-topic">
              Topic <b>*</b>
            </label>
            <textarea
              id="hg-topic"
              value={topic}
              onChange={(event) => setTopic(event.target.value.slice(0, 500))}
              maxLength={500}
              rows={5}
              placeholder="Enter your topic..."
              disabled={loading}
              aria-describedby="hg-topic-count"
            />
            <small id="hg-topic-count" className="hg-counter">
              {topic.length}/500
            </small>
          </div>

          <div className="hg-field">
            <label htmlFor="hg-keywords">
              Related Keywords <span>(optional)</span>
            </label>
            <input
              id="hg-keywords"
              type="text"
              value={keywords}
              onChange={(event) => setKeywords(event.target.value.slice(0, 300))}
              maxLength={300}
              placeholder="e.g. content marketing, SEO, small business"
              disabled={loading}
            />
          </div>

          <div className="hg-grid">
            <div className="hg-field">
              <label htmlFor="hg-tone">
                Tone <b>*</b>
              </label>
              <select
                id="hg-tone"
                value={tone}
                onChange={(event) => setTone(event.target.value)}
                disabled={loading}
              >
                {TONES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="hg-field">
              <label htmlFor="hg-language">
                Language <b>*</b>
              </label>
              <select
                id="hg-language"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                disabled={loading}
              >
                {LANGUAGES.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div className="hg-field hg-count-field">
              <label htmlFor="hg-count">
                Headlines <b>*</b>
              </label>
              <select
                id="hg-count"
                value={count}
                onChange={(event) => setCount(Number(event.target.value))}
                disabled={loading}
              >
                {HEADLINE_COUNTS.map((item) => (
                  <option key={item} value={item}>
                    {item} headlines
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error ? (
            <div className="hg-error" role="alert">
              {error}
            </div>
          ) : null}

          <button className="hg-submit" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="hg-spinner" aria-hidden="true" />
                Generating...
              </>
            ) : (
              <>
                <FaMagic aria-hidden="true" /> Generate Headlines
              </>
            )}
          </button>

          <div className="hg-form-foot">
            <span>
              <FaRobot aria-hidden="true" /> Generated through the secure backend
            </span>
            <button type="button" onClick={handleReset} disabled={loading}>
              <FaRedoAlt aria-hidden="true" /> Reset
            </button>
          </div>
        </form>
      </section>

      {result && headlines.length ? (
        <section
          className="hg-results"
          id="headline-results"
          aria-live="polite"
        >
          <div className="hg-results-head">
            <div>
              <p className="hg-eyebrow">
                <FaLightbulb aria-hidden="true" /> Generated ideas
              </p>
              <h2>Your headlines</h2>
              <span>
                {result.tone || tone} tone · {result.language || language}
              </span>
            </div>
            <div className="hg-actions">
              <button type="button" onClick={handleCopyAll}>
                <FaCopy aria-hidden="true" /> Copy all
              </button>
              <button type="button" className="primary" onClick={handleDownload}>
                <FaDownload aria-hidden="true" /> TXT
              </button>
            </div>
          </div>

         

          <div className="hg-list">
            {headlines.map((item, index) => (
              <article className="hg-card" key={item.id}>
                <div className="hg-number">{String(index + 1).padStart(2, "0")}</div>
                <div className="hg-card-body">
                  <div className="hg-card-meta">
                    <span>{item.type || "Generated"}</span>
                    <span>{item.headline.length} characters</span>
                  </div>
                  <h3>{item.headline}</h3>
                  {item.reason ? <p>{item.reason}</p> : null}
                </div>
                <button
                  type="button"
                  className={`hg-copy ${copiedId === item.id ? "copied" : ""}`}
                  onClick={() => handleCopyOne(item)}
                  aria-label={`Copy headline ${index + 1}`}
                  title="Copy headline"
                >
                  {copiedId === item.id ? (
                    <FaCheck aria-hidden="true" />
                  ) : (
                    <FaCopy aria-hidden="true" />
                  )}
                </button>
              </article>
            ))}
          </div>

          <div className="hg-results-foot">
            <span>
              <FaRegClock aria-hidden="true" /> Generated just now
            </span>
            <button type="button" onClick={handleSubmit} disabled={loading}>
              <FaRedoAlt aria-hidden="true" /> Generate another set
            </button>
          </div>
        </section>
      ) : null}
    </main>
  );
};

const styles = `
  :root {
    --hg-navy: #1f2d4d;
    --hg-orange: #fb5736;
    --hg-orange-dark: #e84527;
    --hg-bg: #fff9f7;
    --hg-border: #dce2ec;
    --hg-text: #505a70;
    --hg-muted: #8c97aa;
    --hg-white: #ffffff;
  }

  * { box-sizing: border-box; }

  .hg-page {
    min-height: 100vh;
    padding: 54px 24px 80px;
    color: var(--hg-navy);
    background:
      radial-gradient(circle at 12% 12%, rgba(251, 87, 54, 0.055), transparent 25%),
      var(--hg-bg);
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .hg-hero {
    width: 1200px;
    margin: 0;
    text-align: center;
  }

  .hg-hero h1 {
    margin: 0 0 18px;
    font-size: clamp(48px, 6.2vw, 76px);
    line-height: 1.03;
    letter-spacing: -2.6px;
    font-weight: 850;
  }

  .hg-hero h1 span { color: var(--hg-orange); }

  .hg-hero p {
    max-width: 940px;
    margin: 0 auto;
    color: #4d5567;
    font-size: 20px;
    line-height: 1.6;
  }

  .hg-shell,
  .hg-results {
    width: min(1120px, 100%);
    margin: 0 auto;
    border: 1px solid var(--hg-border);
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: 0 16px 42px rgba(31, 45, 77, 0.06);
  }

  .hg-form { padding: 34px 32px 30px; }

  .hg-field {
    position: relative;
    margin-bottom: 28px;
  }

  .hg-field label {
    position: absolute;
    z-index: 1;
    top: -10px;
    left: 14px;
    padding: 0 5px;
    color: #151a26;
    background: var(--hg-white);
    font-size: 14px;
    font-weight: 750;
  }

  .hg-field label b { color: var(--hg-orange); }
  .hg-field label span { color: var(--hg-muted); font-weight: 500; }

  .hg-field textarea,
  .hg-field input,
  .hg-field select {
    width: 100%;
    border: 1px solid #cbd4e1;
    border-radius: 7px;
    outline: 0;
    color: #26324a;
    background: var(--hg-white);
    font: inherit;
    transition: border-color .2s ease, box-shadow .2s ease;
  }

  .hg-field textarea {
    min-height: 128px;
    padding: 16px;
    resize: vertical;
    line-height: 1.55;
  }

  .hg-field input,
  .hg-field select { height: 52px; padding: 0 15px; }
  .hg-field select { cursor: pointer; }

  .hg-field textarea:focus,
  .hg-field input:focus,
  .hg-field select:focus {
    border-color: var(--hg-orange);
    box-shadow: 0 0 0 3px rgba(251, 87, 54, 0.12);
  }

  .hg-field textarea:disabled,
  .hg-field input:disabled,
  .hg-field select:disabled { background: #f6f7fa; cursor: not-allowed; }

  .hg-counter {
    position: absolute;
    right: 12px;
    bottom: 8px;
    color: var(--hg-muted);
    font-size: 12px;
  }

  .hg-grid {
    display: grid;
    grid-template-columns: 1fr 1fr .72fr;
    gap: 28px;
  }

  .hg-error {
    margin: -7px 0 18px;
    padding: 12px 14px;
    border: 1px solid #ffd1c8;
    border-radius: 9px;
    color: #c9341b;
    background: #fff0ec;
    font-size: 14px;
  }

  .hg-submit {
    display: flex;
    width: 100%;
    min-height: 60px;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 0;
    border-radius: 999px;
    color: var(--hg-white);
    background: var(--hg-orange);
    box-shadow: 0 7px 14px rgba(251, 87, 54, 0.2);
    font-size: 18px;
    font-weight: 750;
    cursor: pointer;
    transition: transform .18s ease, background .18s ease, box-shadow .18s ease;
  }

  .hg-submit:hover:not(:disabled) {
    transform: translateY(-1px);
    background: var(--hg-orange-dark);
    box-shadow: 0 10px 22px rgba(251, 87, 54, 0.25);
  }

  .hg-submit:disabled { opacity: .72; cursor: wait; }

  .hg-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, .4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: hg-spin .8s linear infinite;
  }

  @keyframes hg-spin { to { transform: rotate(360deg); } }

  .hg-form-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 18px 6px 0;
    color: var(--hg-muted);
    font-size: 12px;
  }

  .hg-form-foot span,
  .hg-form-foot button,
  .hg-results-foot span,
  .hg-results-foot button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .hg-form-foot button,
  .hg-results-foot button {
    border: 0;
    color: #69758b;
    background: transparent;
    font-weight: 650;
    cursor: pointer;
  }

  .hg-results {
    margin-top: 30px;
    padding: 30px;
    scroll-margin-top: 24px;
  }

  .hg-results-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding-bottom: 22px;
    border-bottom: 1px solid #e8ebf1;
  }

  .hg-eyebrow {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 7px;
    color: var(--hg-orange);
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  .hg-results-head h2 { margin: 0 0 5px; font-size: 29px; }
  .hg-results-head > div > span { color: var(--hg-muted); font-size: 14px; }

  .hg-actions { display: flex; gap: 9px; }

  .hg-actions button {
    display: inline-flex;
    min-height: 40px;
    align-items: center;
    gap: 7px;
    padding: 0 14px;
    border: 1px solid #cfd6e1;
    border-radius: 8px;
    color: #536077;
    background: #fff;
    font-weight: 700;
    cursor: pointer;
  }

  .hg-actions button.primary {
    border-color: var(--hg-orange);
    color: #fff;
    background: var(--hg-orange);
  }

  .hg-stats {
    display: grid;
    grid-template-columns: .8fr .8fr 1.4fr;
    gap: 12px;
    margin: 20px 0;
  }

  .hg-stats article {
    min-width: 0;
    padding: 15px 17px;
    border-radius: 11px;
    background: #f6f7f9;
  }

  .hg-stats strong,
  .hg-stats span { display: block; }
  .hg-stats strong { overflow: hidden; text-overflow: ellipsis; font-size: 18px; white-space: nowrap; }
  .hg-stats span { margin-top: 4px; color: var(--hg-muted); font-size: 11px; text-transform: uppercase; }

  .hg-list { display: grid; gap: 12px; }

  .hg-card {
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) 42px;
    align-items: start;
    gap: 15px;
    padding: 19px;
    border: 1px solid #e1e5ec;
    border-radius: 13px;
    background: #fff;
    transition: border-color .18s ease, transform .18s ease;
  }

  .hg-card:hover { transform: translateY(-1px); border-color: #f4a999; }

  .hg-number {
    display: grid;
    width: 43px;
    height: 43px;
    place-items: center;
    border-radius: 11px;
    color: var(--hg-orange);
    background: #fff0ec;
    font-size: 13px;
    font-weight: 850;
  }

  .hg-card-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  .hg-card-meta span {
    padding: 4px 8px;
    border-radius: 999px;
    color: #6f7788;
    background: #f3f5f8;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .035em;
  }

  .hg-card h3 {
    margin: 0;
    color: #202a40;
    font-size: 19px;
    line-height: 1.38;
  }

  .hg-card p { margin: 8px 0 0; color: #6e788c; font-size: 13px; line-height: 1.55; }

  .hg-copy {
    display: grid;
    width: 38px;
    height: 38px;
    place-items: center;
    border: 1px solid #d8dee8;
    border-radius: 9px;
    color: #657087;
    background: #fff;
    cursor: pointer;
  }

  .hg-copy:hover,
  .hg-copy.copied { border-color: var(--hg-orange); color: var(--hg-orange); background: #fff7f5; }

  .hg-results-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-top: 20px;
    color: var(--hg-muted);
    font-size: 12px;
  }

  .hg-results-foot button { color: var(--hg-orange); }

  @media (max-width: 820px) {
    .hg-page { padding: 38px 16px 60px; }
    .hg-hero { margin-bottom: 36px; }
    .hg-hero h1 { font-size: clamp(42px, 12vw, 62px); letter-spacing: -1.8px; }
    .hg-hero p { font-size: 17px; }
    .hg-grid { grid-template-columns: 1fr 1fr; }
    .hg-count-field { grid-column: 1 / -1; }
  }

  @media (max-width: 600px) {
    .hg-form,
    .hg-results { padding: 22px 17px; }
    .hg-grid { grid-template-columns: 1fr; gap: 0; }
    .hg-count-field { grid-column: auto; }
    .hg-results-head,
    .hg-form-foot,
    .hg-results-foot { align-items: flex-start; flex-direction: column; }
    .hg-actions { width: 100%; }
    .hg-actions button { flex: 1; justify-content: center; }
    .hg-stats { grid-template-columns: 1fr 1fr; }
    .hg-stats article:last-child { grid-column: 1 / -1; }
    .hg-card { grid-template-columns: 39px minmax(0, 1fr); padding: 15px; gap: 11px; }
    .hg-number { width: 37px; height: 37px; }
    .hg-copy { grid-column: 2; width: 100%; }
  }
`;

export default HeadlineGenerator;
